/**
 * Turns the staff /cooperatives/dashboard response into what the staff home page shows.
 * Pure functions, no React, so the rules are easy to test.
 */
import { daysBetween, formatNaira, parseDateOnly } from "../../../../utils/dashboardFormat";

export const STAFF_ROUTES = {
  members: "/super_admin/member_management/members",
  pendingMembers: "/super_admin/member_management/pending_member",
  staff: "/super_admin/staff_registration/view_staff",
  contributions: "/super_admin/contribution/contribution_history",
  savings: "/super_admin/contribution/contribution_history?category_kind=savings",
  shares: "/super_admin/contribution/contribution_history?category_kind=shares",
  withdrawals: "/super_admin/contribution/withdrawal_requests",
  loanStatus: "/super_admin/loan_management/loan_status",
  repayments: "/super_admin/loan_management/loan_repayment",
  events: "/super_admin/event_calendar",
};

/** Days left on a subscription before it is shown as a warning. */
export const SUBSCRIPTION_WARN_DAYS = 14;

const countOf = (block) => Number(block?.count) || 0;

/**
 * Tiles for the "Needs attention" row.
 * Returns null when the API does not send `attention` yet (the row is then hidden),
 * and skips any block the API leaves null (the staff member's role cannot see it).
 */
export function buildAttentionTiles(attention) {
  if (!attention || typeof attention !== "object") return null;

  const tiles = [];

  if (attention.overdue_repayments) {
    const count = countOf(attention.overdue_repayments);
    tiles.push({
      key: "overdue",
      title: "Overdue repayments",
      count,
      detail: count > 0 ? `${formatNaira(attention.overdue_repayments.amount)} outstanding` : "None overdue",
      tone: count > 0 ? "bad" : "ok",
      to: STAFF_ROUTES.repayments,
    });
  }
  if (attention.pending_loan_requests) {
    const count = countOf(attention.pending_loan_requests);
    tiles.push({
      key: "loans",
      title: "Loan requests to approve",
      count,
      detail: count > 0 ? `${formatNaira(attention.pending_loan_requests.amount)} requested` : "Nothing waiting",
      tone: count > 0 ? "warn" : "ok",
      to: STAFF_ROUTES.loanStatus,
    });
  }
  if (attention.pending_members) {
    const count = countOf(attention.pending_members);
    tiles.push({
      key: "members",
      title: "New members to activate",
      count,
      detail: count > 0 ? "Waiting for approval" : "Nothing waiting",
      tone: count > 0 ? "warn" : "ok",
      to: STAFF_ROUTES.pendingMembers,
    });
  }
  if (attention.pending_withdrawals) {
    const count = countOf(attention.pending_withdrawals);
    tiles.push({
      key: "withdrawals",
      title: "Withdrawals to approve",
      count,
      detail: count > 0 ? `${formatNaira(attention.pending_withdrawals.amount)} requested` : "Nothing waiting",
      tone: count > 0 ? "warn" : "ok",
      to: STAFF_ROUTES.withdrawals,
    });
  }

  return tiles;
}

export function totalAttention(tiles) {
  return (tiles || []).reduce((sum, tile) => sum + tile.count, 0);
}

export function buildMoneyTiles(d = {}) {
  return [
    {
      key: "contribution",
      label: "Total contribution",
      value: formatNaira(d.total_contribution ?? d.total_contributed ?? d.totalContribution ?? 0),
      to: STAFF_ROUTES.contributions,
    },
    {
      key: "savings",
      label: "Savings",
      value: formatNaira(d.total_savings ?? d.availableSavings ?? 0),
      to: STAFF_ROUTES.savings,
    },
    { key: "shares", label: "Shares", value: formatNaira(d.total_shares ?? 0), to: STAFF_ROUTES.shares },
    {
      key: "withdrawn",
      label: "Withdrawn",
      value: formatNaira(d.totalWithdrawn ?? 0),
      to: STAFF_ROUTES.withdrawals,
    },
  ];
}

export function buildLoanTotals(d = {}) {
  return [
    { key: "disbursed", title: "Disbursed", value: formatNaira(d.loanDisbursed ?? 0), to: STAFF_ROUTES.loanStatus },
    { key: "unpaid", title: "Unpaid", value: formatNaira(d.unpaidLoans ?? 0), to: STAFF_ROUTES.repayments },
  ];
}

export function buildPeopleRows(d = {}) {
  const members = d.activeMembers ?? d.onboardedMembers ?? 0;
  const staff = d.onboardedStaff ?? 0;
  return [
    { key: "members", title: "Active members", value: Number(members) || 0, to: STAFF_ROUTES.members },
    { key: "staff", title: "Onboarded staff", value: Number(staff) || 0, to: STAFF_ROUTES.staff },
  ];
}

/** Plan name, how long is left, and how worried to look about it. */
export function subscriptionStatus(subscription, now = new Date()) {
  if (!subscription) return null;

  let days = subscription.days_remaining;
  const expiry = parseDateOnly(subscription.expires_at);
  if ((days === undefined || days === null) && expiry) {
    days = daysBetween(now, expiry);
  }
  days = days === undefined || days === null ? null : Number(days);

  let tone = "ok";
  let label = "Active";
  if (days !== null) {
    if (days < 0) {
      tone = "bad";
      label = `Expired ${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"} ago`;
    } else if (days === 0) {
      tone = "bad";
      label = "Expires today";
    } else {
      tone = days <= SUBSCRIPTION_WARN_DAYS ? "warn" : "ok";
      label = `${days} ${days === 1 ? "day" : "days"} left`;
    }
  }

  return {
    planName: subscription.plan?.name || "",
    days,
    tone,
    label,
    expiresLabel: expiry
      ? expiry.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
      : "",
  };
}

/** SMS balance in naira. The API sends it as a string in naira, or as kobo. */
export function smsBalanceNaira(d = {}) {
  if (d.smsBalanceNaira !== undefined && d.smsBalanceNaira !== null) {
    const n = Number(d.smsBalanceNaira);
    if (Number.isFinite(n)) return n;
  }
  const kobo = Number(d.smsBalanceKobo ?? d.smsBalance ?? 0);
  return Number.isFinite(kobo) ? kobo / 100 : 0;
}

/** "Sat 10 Oct" for a Y-m-d string. */
export function formatShortDate(value) {
  const date = parseDateOnly(value);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

/** A meeting as the card shows it. */
export function toMeetingRow(meeting, index = 0) {
  const start = formatShortDate(meeting?.start_date);
  const end = formatShortDate(meeting?.end_date);
  const dateLabel = start && end && end !== start ? `${start} to ${end}` : start || "Date to be confirmed";
  const timeLabel =
    meeting?.start_time && meeting?.end_time
      ? `${meeting.start_time} - ${meeting.end_time}`
      : meeting?.start_time || "";
  return {
    id: meeting?.id ?? `meeting-${index}`,
    name: meeting?.name || "Untitled meeting",
    dateLabel,
    timeLabel,
  };
}

/** The checklist as "3 of 5 done" plus the steps still open. */
export function summariseChecklist(checklist) {
  const items = Array.isArray(checklist?.items) ? checklist.items : [];
  const done = items.filter((item) => item.status).length;
  return {
    total: items.length,
    done,
    open: items.filter((item) => !item.status),
    progress: Number(checklist?.progress) || 0,
    complete: items.length > 0 && done === items.length,
  };
}
